import { useState, useEffect, useRef } from 'react';
import { Select, Avatar, Space, Tag, Typography, Spin, Alert } from 'antd';
import { DeleteOutlined, LinkOutlined, HolderOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import axiosInstance from '../apis/axiosInstance';
import { BASE_URL } from '../apis/constant';

const { Option } = Select;
const { Text } = Typography;

const RelatedProductsSection = ({ 
    currentProductId, 
    updateRelatedProducts, 
    initialRelatedProducts = [] 
}) => {
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [selectValues, setSelectValues] = useState(['']);
    const [isInitializing, setIsInitializing] = useState(true);
    
    const initialRenderRef = useRef(true);
    const skipUpdateRef = useRef(false);

    // Fetch all products
    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            setError(null);

            try {
                const requestData = currentProductId ? { product_id: currentProductId } : {};
                const response = await axiosInstance.post('/api/get_all_product_for_related_product/', 
                    requestData, 
                    { headers: { 'Content-Type': 'application/json' } }
                );

                if (response.data?.data) {
                    const transformedProducts = response.data.data.map(product => ({
                        id: product.productId.toString(),
                        name: product.productName,
                        image: product.image,
                        category: product.category
                    }));
                    setAllProducts(transformedProducts);
                } else {
                    setAllProducts([]);
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch products');
                setAllProducts([]);
            } finally {
                setLoading(false);
                setHasFetched(true);
                setIsInitializing(false);
            }
        };

        fetchProducts();
    }, [currentProductId]);

    // Initialize with initial related products
    useEffect(() => {
        if (!isInitializing && hasFetched && initialRelatedProducts.length > 0 && allProducts.length > 0) {
            skipUpdateRef.current = true;
            
            const initialIds = initialRelatedProducts
                .map(rp => rp?.id?.toString())
                .filter(id => id && id !== 'undefined' && id !== 'null');
            
            if (initialIds.length > 0) {
                setSelectValues([...initialIds, '']);
            }
            
            setTimeout(() => {
                skipUpdateRef.current = false;
            }, 100);
        }
    }, [isInitializing, hasFetched, initialRelatedProducts, allProducts]);

    // Get selected products array
    const getSelectedProducts = () => {
        return selectValues
            .filter(value => value && value !== '')
            .map((value, index) => {
                const product = allProducts.find(p => p.id === value);
                return product ? { ...product, order: index } : null;
            })
            .filter(Boolean);
    };

    // Get filtered products for dropdown (excluding already selected ones)
    const getFilteredProducts = (currentSelectIndex) => {
        const currentValue = selectValues[currentSelectIndex];
        const otherSelectedValues = selectValues
            .filter((value, index) => index !== currentSelectIndex && value && value !== '');

        return allProducts.filter(p =>
            !otherSelectedValues.includes(p.id) || p.id === currentValue
        );
    };

    // Handle product selection
    const handleSelectChange = (value, index) => {
        const newSelectValues = [...selectValues];
        newSelectValues[index] = value;
        
        const cleanValues = newSelectValues.filter((val, idx) => 
            val !== '' || idx === newSelectValues.length - 1
        );
        
        if (cleanValues[cleanValues.length - 1] !== '') {
            cleanValues.push('');
        }
        
        setSelectValues(cleanValues);
    };

    // Remove a selected product
    const handleRemoveProduct = (productId, index) => {
        const newSelectValues = [...selectValues];
        newSelectValues.splice(index, 1);
        
        if (newSelectValues.length === 0 || newSelectValues[newSelectValues.length - 1] !== '') {
            newSelectValues.push('');
        }
        
        setSelectValues(newSelectValues);
    };

    // Handle drag and drop reordering
    const onDragEnd = (result) => {
        if (!result.destination) return;

        const selectedProducts = getSelectedProducts();
        const reorderedItems = Array.from(selectedProducts);
        const [removed] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, removed);

        const newSelectValues = reorderedItems.map(item => item.id);
        newSelectValues.push('');
        
        setSelectValues(newSelectValues);
    };

    // Notify parent component about related products changes
    useEffect(() => {
        if (isInitializing || skipUpdateRef.current || !hasFetched) {
            return;
        }

        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            return;
        }

        const selectedProducts = getSelectedProducts();
        
        if (selectedProducts.length > 0) {
            const productData = selectedProducts.map((product, index) => ({
                id: product.id,
                product_id: product.id,
                product_name: product.name,
                name: product.name,
                image: product.image,
                category: product.category,
                order: index
            }));

            if (updateRelatedProducts) {
                updateRelatedProducts(productData);
            }
        } else if (updateRelatedProducts) {
            updateRelatedProducts([]);
        }
    }, [selectValues, hasFetched, isInitializing]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            initialRenderRef.current = true;
            skipUpdateRef.current = false;
        };
    }, [currentProductId]);

    // Filter function for search
    const filterOption = (input, option) => {
        const product = allProducts.find(p => p.id === option.value);
        if (!product) return false;

        const searchText = input.toLowerCase();
        return (
            product.name.toLowerCase().includes(searchText) ||
            product.category.toLowerCase().includes(searchText) ||
            product.id.toLowerCase().includes(searchText)
        );
    };

    // Render dropdown option
    const renderSelectOption = (product) => (
        <Space align="start" style={{ padding: '4px 0' }}>
            <Avatar
                src={product.image ? `${BASE_URL}${product.image}` : ''}
                shape="square"
                size={40}
                style={{ borderRadius: '6px', flexShrink: 0, backgroundColor: '#f5f5f5' }}
            />
            <div className="flex flex-col" style={{ flex: 1 }}>
                <Text strong style={{ lineHeight: 1.3, marginBottom: 2, fontSize: '13px' }}>
                    {product.name}
                </Text>
                <Text type="secondary" className="text-xs" style={{ lineHeight: 1.2 }}>
                    {product.category}
                </Text>
            </div>
        </Space>
    );

    const selectedProducts = getSelectedProducts();
    const emptySelectIndexes = selectValues
        .map((value, index) => value === '' ? index : -1)
        .filter(index => index !== -1);

    // Loading state
    if (loading) {
        return (
            <div className='w-full px-5 md:px-6 mb-4'>
                <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <LinkOutlined /> Related Products
                    </h3>
                </div>
                <div className="flex justify-center items-center py-8">
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className='w-full px-5 md:px-6 mb-4'>
                <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <LinkOutlined /> Related Products
                    </h3>
                </div>
                <Alert message="Error Loading Products" description={error} type="error" showIcon />
            </div>
        );
    }

    return (
        <div className='w-full px-5 md:px-6 mb-4'>
            <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <LinkOutlined /> Related Products
                    <Tag className="ml-2" color="blue">{selectedProducts.length} selected</Tag>
                </h3>
            </div>

            {hasFetched && allProducts.length === 0 && !loading && (
                <Alert 
                    message="No products available" 
                    description="There are no other products to select as related products." 
                    type="info" 
                    showIcon 
                    className="mb-4" 
                />
            )}

            {/* Drag and drop area for selected products */}
            {selectedProducts.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="related-products">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 mb-4">
                                {selectedProducts.map((product, index) => (
                                    <Draggable key={product.id} draggableId={product.id} index={index}>
                                        {(provided, snapshot) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className={`${snapshot.isDragging ? 'opacity-80' : ''}`}>
                                                <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                                                    <Space align="center" size={8}>
                                                        <div {...provided.dragHandleProps} className="flex items-center">
                                                            <HolderOutlined className="text-gray-400 cursor-move text-sm mr-2" />
                                                        </div>

                                                        <Avatar
                                                            src={product.image ? `${BASE_URL}${product.image}` : ''}
                                                            shape="square"
                                                            size={40}
                                                            style={{ borderRadius: '6px', border: '1px solid #f0f0f0', backgroundColor: '#f5f5f5' }}
                                                        />

                                                        <div className="flex flex-col">
                                                            <Text strong className="text-gray-800" style={{ fontSize: '14px', lineHeight: 1.3 }}>
                                                                {product.name}
                                                            </Text>
                                                            <Space size="small" style={{ marginTop: 1 }}>
                                                                <Tag color="blue" className="text-xs" style={{ margin: 0, padding: '0 6px' }}>
                                                                    {product.category}
                                                                </Tag>
                                                                <Text type="secondary" className="text-xs">ID: {product.id}</Text>
                                                            </Space>
                                                        </div>
                                                    </Space>

                                                    <DeleteOutlined
                                                        className="text-gray-400 hover:text-red-500 cursor-pointer ml-2"
                                                        onClick={() => {
                                                            const actualIndex = selectValues.findIndex(val => val === product.id);
                                                            handleRemoveProduct(product.id, actualIndex);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {/* Dropdown for adding new related products */}
            {allProducts.length > 0 && (
                <div className="space-y-2">
                    {emptySelectIndexes.map((index) => (
                        <Select
                            key={`empty-${index}`}
                            style={{ width: '100%' }}
                            placeholder="Search or select a related product..."
                            size="middle"
                            showSearch
                            filterOption={filterOption}
                            optionFilterProp="children"
                            value={selectValues[index] || undefined}
                            onChange={(val) => handleSelectChange(val, index)}
                            dropdownStyle={{ padding: '8px 0', borderRadius: '8px' }}
                            notFoundContent={<div className="py-2 text-center text-gray-500">No matching products found</div>}
                        >
                            {getFilteredProducts(index).map(product => (
                                <Option key={product.id} value={product.id} style={{ padding: '8px 12px' }}>
                                    {renderSelectOption(product)}
                                </Option>
                            ))}
                        </Select>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RelatedProductsSection;